<template>
  <SoWidget :loading="loading" data-testid="fsr-widget">
    <div class="d-flex gap-1 align-center">
      <SoAvatar
        :type="meterpoint.isElectricity ? 'electric' : 'gas'"
        data-testid="fsr-avatar"
      />
      <h6 class="font-weight-medium" data-testid="fsr-identifier">
        {{ meterpoint.meterpointType }} - {{ meterpoint.identifier }}
      </h6>
      <v-spacer />
      <SoStatus :status="meterpoint.dccStatus" data-testid="fsr-dcc-status" />
    </div>
    <SoButton
      :disabled="FetchSmartReadMutator.isSuccess"
      title="Fetch Smart Reading"
      class="mt-4 mr-2"
      data-testid="fsr-btn"
      @click="FetchSmartReadMutator.mutate()"
    />
    <SoTooltip
      text="Refresh last smart reading"
      :disabled="isFetchLatestReadsDisabled"
    >
      <SoButton
        :disabled="isFetchLatestReadsDisabled"
        icon="refresh"
        data-testid="fsr-refresh-btn"
        @click="getLatestRead()"
      />
    </SoTooltip>
    <SoAlert
      v-if="FetchSmartReadMutator.isSuccess && !hasOneOffReadRequestFailedError"
      class="mt-2"
      data-testid="fsr-success-alert"
    >
      A smart read can take up to 5 minutes to come through onto the account.
      After a few minutes, please use the refresh button and check the most
      recent smart reading.
    </SoAlert>
    <SoAlert
      v-else-if="
        FetchSmartReadMutator.isError || hasOneOffReadRequestFailedError
      "
      class="mt-2"
      data-testid="fsr-error-alert"
      type="error"
    >
      {{
        hasOneOffReadRequestFailedError
          ? `
            There is already a smart meter read request open. Refresh to check
            if the new reading has come through on the account.
          `
          : FetchSmartReadMutator.error
      }}
    </SoAlert>
    <p class="mt-4" data-testid="fsr-latest-read">
      Last smart reading (SMR) received was
      {{ meterpoint.latestSmartRead?.cumulative
      }}{{ meterpoint.latestSmartRead?.unit }}
      <b>{{ meterpoint.latestSmartRead?.rateName }}</b> on
      {{ formatDate(meterpoint.latestSmartRead?.date) }}
      <a
        v-if="FetchSmartReadMutator.isSuccess"
        href="javascript:void(0)"
        class="text-primary"
        @click="reset()"
        >reset</a
      >
    </p>
  </SoWidget>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import {
    FetchSmartReadMutator,
    ONE_OFF_READ_REQUEST_FAILED,
  } from "@/mutators/meterpoint/FetchSmartReadMutator"
  import { getFullDateAndTime } from "@/helpers/dateHelper"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import SoWidget from "@/components/SoWidget.vue"
  import SoAvatar from "@/components/SoAvatar.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoAlert from "@/components/SoAlert.vue"

  export default defineComponent({
    components: {
      SoWidget,
      SoAvatar,
      SoButton,
      SoTooltip,
      SoStatus,
      SoAlert,
    },
    props: {
      meterpoint: {
        type: Object as PropType<IMeterpoint>,
        required: true,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        current: useCurrentAccountStore(),
        FetchSmartReadMutator: FetchSmartReadMutator.fetchSmartRead(
          this.meterpoint.identifier,
        ),
        formatDate: getFullDateAndTime,
        isFetchLatestReadsDisabled: false,
      }
    },
    computed: {
      loading() {
        return this.FetchSmartReadMutator.isPending
      },
      hasOneOffReadRequestFailedError() {
        return JSON.stringify(this.FetchSmartReadMutator?.data)?.includes(
          ONE_OFF_READ_REQUEST_FAILED,
        )
      },
    },
    methods: {
      getLatestRead() {
        this.isFetchLatestReadsDisabled = true
        this.current.meterpoints.refetch()
        setTimeout(() => {
          this.isFetchLatestReadsDisabled = false
        }, 30000)
      },
      reset() {
        this.FetchSmartReadMutator.reset()
        this.isFetchLatestReadsDisabled = false
      },
    },
  })
</script>
